import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Message } from '../../contexts/ConversationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { MessageBubble } from './MessageBubble';
import { PhaseTrailBlock } from './PhaseTrailBlock';
import type { PhaseEvent } from '../../hooks/usePersonaToolProgress';

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  loadingStatus?: string;
  /** Structured phase-event stream for the currently-loading turn. Each
   *  WS tool/peer/heartbeat transition contributes one record. Replaces
   *  the legacy string-derived `statusHistory` pipeline — individual peer
   *  terminal transitions no longer get swallowed by aggregate views. */
  phaseEvents?: PhaseEvent[];
  showTimestamps?: boolean;
  className?: string;
  currentModel?: string | null;
  sessionId?: string;
  personaId?: string;
  personaName?: string;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading = false,
  loadingStatus,
  phaseEvents,
  showTimestamps = true,
  className = '',
  currentModel,
  sessionId,
  personaId,
  personaName
}) => {
  const { darkMode } = useTheme();
  const loadingRef = useRef<HTMLDivElement>(null);
  const latestMessageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(messages.length);
  const [statusExpanded, setStatusExpanded] = useState(true);
  // Tracks the id of the last assistant message we snapped to the top,
  // so a fresh assistant arrival only fires the pin-to-top once per
  // message (not on every unrelated state tick).
  const lastPinnedAsstIdRef = useRef<string | null>(null);
  // Tracks whether the user has actively scrolled away from the bottom.
  // Auto-scrolls are suppressed while this is true so we don't yank
  // someone who's reading history. Reset by a send or by a manual
  // scroll back down.
  const userScrolledAwayRef = useRef(false);

  // Re-expand on each new turn so the user sees the trail by default.
  useEffect(() => {
    if (isLoading) setStatusExpanded(true);
  }, [isLoading]);

  // Structured live trail comes from the hook's phaseEvents prop. Each
  // WS transition is already its own record (no aggregate-view collapse)
  // so this component no longer needs to parse / dedup strings. On page
  // reload, `phaseEvents` is empty and the persisted trail renders via
  // MessageBubble reading `metadata.phase_events` — one code path for
  // both live and rehydrated views.
  const liveEvents = phaseEvents ?? [];

  // Only auto-scroll if user is already near the bottom (not reading history)
  const isNearBottom = useCallback(() => {
    if (!containerRef.current) return true;
    const c = containerRef.current;
    return c.scrollHeight - c.scrollTop - c.clientHeight < 150;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (!containerRef.current) return;
    const c = containerRef.current;
    c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
  }, []);

  // Scroll the latest-message wrapper so its TOP aligns with the
  // viewport top. Used on turn-end: the new assistant bubble reads
  // from the top, and its PersistedPhaseTrail (which renders as the
  // first child of the wrapper, collapsed by default) stays visible
  // just above as a "phase bubble" summary the operator can expand
  // on demand.
  const scrollLatestToTop = useCallback(() => {
    const el = latestMessageRef.current;
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);

  // Detect when the user manually scrolls away from the bottom. If
  // they do, auto-scrolls pause until they scroll back down. Sends
  // reset the state (see user-msg scroll below) because a brand-new
  // turn is a fresh attention target.
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const onScroll = () => {
      const atBottom = c.scrollHeight - c.scrollTop - c.clientHeight < 80;
      userScrolledAwayRef.current = !atBottom;
    };
    c.addEventListener('scroll', onScroll, { passive: true });
    return () => c.removeEventListener('scroll', onScroll);
  }, []);

  // When loading starts (user sent a message), ALWAYS scroll to the
  // bottom — they just hit send and we want the pending-action
  // indicator in view. Also resets the user-scrolled-away flag
  // because a new turn starts with the user's attention at the end.
  useEffect(() => {
    if (isLoading) {
      userScrolledAwayRef.current = false;
      setTimeout(scrollToBottom, 50);
    }
  }, [isLoading, scrollToBottom]);

  // During a running turn, keep the phase trail's TAIL in view as new
  // events stream in. Uses scrollToBottom so if the trail outgrows
  // the viewport the most recent events stay visible (Alan's "always
  // show the bottom" ask). Gated by userScrolledAwayRef so we don't
  // yank someone who's reading history.
  const liveEventCount = phaseEvents?.length ?? 0;
  useEffect(() => {
    if (!isLoading) return;
    if (userScrolledAwayRef.current) return;
    const h = window.setTimeout(scrollToBottom, 30);
    return () => window.clearTimeout(h);
  }, [isLoading, liveEventCount, scrollToBottom]);

  // Turn-end pin: the moment a NEW assistant message lands, snap its
  // wrapper's top to the viewport top. The wrapper contains the
  // PersistedPhaseTrail (collapsed by default) followed by the
  // MessageBubble — so the user sees: [phase trail ▸ collapsed] at
  // the top, then the new answer reading cleanly down from there.
  //
  // Keyed on message id (not length) so we fire exactly once per
  // fresh arrival, even if isLoading / phaseEvents ticks ahead of
  // the messages update.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') return;
    if (last.id === lastPinnedAsstIdRef.current) return;
    lastPinnedAsstIdRef.current = last.id;
    if (userScrolledAwayRef.current) return;
    // Two frames of delay so the DOM has the wrapper mounted AND the
    // PersistedPhaseTrail has committed its initial collapsed height;
    // scrollIntoView on a still-growing element lands off-target.
    const h = window.setTimeout(scrollLatestToTop, 120);
    return () => window.clearTimeout(h);
  }, [messages, scrollLatestToTop]);

  // Keep the count ref in sync for any legacy consumer (and for
  // parity with the prior pattern — other effects can lean on it).
  useEffect(() => {
    prevMessageCountRef.current = messages.length;
  }, [messages.length]);

  // Format model name for display
  const formatModelName = (modelId: string | null | undefined) => {
    if (!modelId) return null;
    const parts = modelId.split('/');
    return parts.length > 1 ? parts[1] : modelId;
  };

  const visibleMessages = messages.filter(m => m.role !== 'system');

  return (
    <div ref={containerRef} className={`flex-grow overflow-y-auto p-4 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} ${className}`}>
      {visibleMessages.map((message, idx) => {
        const isLast = idx === visibleMessages.length - 1;
        return (
          <React.Fragment key={message.id}>
            {/* Persisted phase trail lives inside MessageBubble for every
                assistant message with `metadata.phase_events` — works
                for live-just-landed messages AND reloaded sessions, for
                every tool (not just consult_peers). */}
            <div ref={isLast ? latestMessageRef : undefined}>
              <MessageBubble
                message={message}
                showTimestamp={showTimestamps}
                modelName={
                  message.role === 'assistant'
                    ? formatModelName(
                        // Prefer the model that actually produced this message, so
                        // historical bubbles aren't retroactively relabeled when the
                        // session's current model changes.
                        // Shape differs between call sites:
                        //   historical (blackboard): top-level `model_used`
                        //   immediate response:      `metadata.model` (raw from LLM)
                        //   legacy / none set:       currentModel
                        (message as any)?.model_used ??
                          (message as any)?.metadata?.model_used ??
                          (message as any)?.metadata?.model ??
                          currentModel
                      )
                    : undefined
                }
                sessionId={sessionId}
                personaId={personaId}
                personaName={personaName}
              />
            </div>
          </React.Fragment>
        );
      })}

      {/* Live loading indicator — shows between user message and pending
          response. PhaseTrailBlock renders directly from the hook's
          phaseEvents stream; every WS tool/peer/heartbeat transition is
          already its own record (no aggregate-view collapse). */}
      {isLoading && (
        <div ref={loadingRef}>
          <PhaseTrailBlock
            isLoading={true}
            loadingStatus={loadingStatus}
            events={liveEvents}
            expanded={statusExpanded}
            setExpanded={setStatusExpanded}
            darkMode={darkMode}
          />
        </div>
      )}
    </div>
  );
};
